import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Tree } from 'react-arborist';
import PropTypes from 'prop-types';
import { FaDatabase, FaFolder, FaFolderOpen, FaProjectDiagram, FaEye, FaCog } from 'react-icons/fa';
import { FiEdit2, FiXCircle, FiLink, FiTrash2, FiPlus, FiSquare, FiX, FiUser, FiActivity, FiSearch, FiHelpCircle, FiRefreshCw, FiMonitor, FiChevronsLeft } from 'react-icons/fi';
import { BiTable } from 'react-icons/bi';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import { DbmsIcon } from '../utils/dbmsIcon';
import Elapsed from './Elapsed';
import { sessionDisplayName, sessionSid } from '../utils/sessionDisplay';
import styles from './SchemaExplorer.module.css'; // CSS Modules 사용

// App.js에서 initialData를 prop으로 받으므로 아래 목업 데이터는 제거합니다.
// const initialData = [ ... ];

// 세션 카테고리별 아이콘 (Phase 2)
function SessionCategoryIcon({ category, status }) {
    const isCollectActive = (category === 'system-collect') && (status === 'running');
    const className = isCollectActive
        ? `${styles.icon} ${styles.spinning}`
        : styles.icon;
    if (category === 'user')           return <FiUser className={className} style={{ color: '#3b82f6' }} />;
    if (category === 'system-collect') return <FiActivity className={className} style={{ color: '#10b981' }} />;
    if (category === 'system-query')   return <FiSearch className={className} style={{ color: '#8b5cf6' }} />;
    // 독립 연결 창 전용 — 창을 닫아도 남아 다시 열 때 쓴다(useOwnConnSession). 편집기 세션과 구분돼 보여야
    // 「안 쓰는 세션」을 닫을 때 무엇인지 안다.
    if (category === 'monitor')        return <FiMonitor className={className} style={{ color: '#f59e0b' }} />;
    return <FiUser className={className} style={{ color: '#9ca3af' }} />;
}

// 노드 렌더러 (props: node, style, dragHandle, tree가 react-arborist에 의해 주입됨)
function Node({ node, style, dragHandle, tree }) {
    const { t } = useTranslation();
    const Icon = () => {
        switch (node.data.type) {
            case 'connection':
                return <DbmsIcon dbType={node.data.dbType} className={styles.icon} size={16} />;
            case 'schema':
                return node.isOpen
                    ? <FaFolderOpen className={styles.icon} style={{ color: '#f39c12' }} />
                    : <FaFolder className={styles.icon} style={{ color: '#f39c12' }} />;
            case 'folder':
                return node.isOpen
                    ? <FaFolderOpen className={styles.icon} style={{ color: '#8e8e93' }} />
                    : <FaFolder className={styles.icon} style={{ color: '#8e8e93' }} />;
            // Phase 2: 세션 트리용 가상 폴더
            // 색은 인라인 style 대신 CSS 클래스로 뺐다 — 다크 모드에서 너무 밝다는 피드백 반영,
            // [data-theme="dark"] 로 톤을 낮추려면 인라인 style 로는(항상 이 값으로 고정) 불가능.
            case 'sessions-folder':
                return node.isOpen
                    ? <FaFolderOpen className={`${styles.icon} ${styles.sessionsFolderIcon}`} />
                    : <FaFolder className={`${styles.icon} ${styles.sessionsFolderIcon}`} />;
            case 'schemas-folder':
                return node.isOpen
                    ? <FaFolderOpen className={`${styles.icon} ${styles.schemasFolderIcon}`} />
                    : <FaFolder className={`${styles.icon} ${styles.schemasFolderIcon}`} />;
            case 'session':
                return <SessionCategoryIcon category={node.data.category} status={node.data.status} />;
            case 'table':
                return <BiTable className={styles.icon} style={{ color: '#27ae60' }} />;
            case 'view':
                return <FaEye className={styles.icon} style={{ color: '#3498db' }} />;
            case 'procedure':
                return <FaCog className={styles.icon} style={{ color: '#e67e22' }} />;
            case 'placeholder':
                return <AiOutlineLoading3Quarters className={`${styles.icon} ${styles.spinning}`} style={{ color: '#95a5a6' }} />;
            default: return null;
        }
    };

    const handleClick = () => {
        // react-arborist가 내부적으로 onActivate를 호출해줄 것임
    };

    const handleERDClick = (e) => {
        e.stopPropagation(); // 이벤트 버블링 방지
        if (tree.props.onERDClick && node.data.type === 'schema') {
            tree.props.onERDClick(node.data);
        }
    };

    // 폴더 라벨은 표시 시점에 번역한다 — reducer 가 구운 name 은 조회 당시 언어로
    // persist 에 고정되어 언어를 바꿔도 "Tables (11)" 처럼 남는다. 구운 name 은
    // 키가 없는 옛 데이터의 폴백으로만 쓴다.
    const folderLabelKeys = { tables: 'common:tree.tables', views: 'common:tree.views', procedures: 'common:tree.procs' };
    const displayName = node.data.type === 'folder' && folderLabelKeys[node.data.folderType]
        ? t(folderLabelKeys[node.data.folderType], { n: (node.data.children || []).length })
        : node.data.name;

    let nodeContent = (
        <div className={styles.nodeContent}>
            <span className={styles.nodeName}>{displayName}</span>
            {node.data.type === 'schema' && (
                // title 은 span(실제 DOM 엘리먼트)에 둔다 — react-icons 는 title prop 을
                // <svg> 의 title ATTRIBUTE 가 아니라 자식 <title> TAG 로 렌더링해서,
                // App.js 의 전역 [title]→커스텀 툴팁 스윕(el.hasAttribute('title'))이 못 잡는다.
                // 그 결과 다른 메뉴와 다르게 브라우저 네이티브(다크모드 미적용) 툴팁으로 새 있었다.
                <span className={styles.iconTipWrap} title={t('common:exp.viewErd')}>
                    <FaProjectDiagram
                        className={styles.erdIcon}
                        onClick={handleERDClick}
                        style={{ color: '#8e44ad', marginLeft: '8px', cursor: 'pointer' }}
                    />
                </span>
            )}
        </div>
    );

    // Phase 2: 세션 관련 노드 분기
    if (node.data.type === 'session') {
        const s = node.data;
        const isRunning = s.status === 'running';
        const isCancelling = s.status === 'cancelling';
        const dotClass = isRunning ? styles.sessionStatusDotRunning
            : isCancelling ? styles.sessionStatusDotCancelling
            : styles.sessionStatusDotIdle;
        // "세션 N" — 예전엔 라벨이 없으면 "세션 (sid=-)" 로 SID 가 없는 DB 에도 sid 를 띄웠다
        const labelText = sessionDisplayName(s, t);
        const sid = sessionSid(s);
        // capability 조회 — dbType 별 manifest.supportsFeatures (Phase A)
        const dbmsByType = tree.props.dbmsByType || {};
        const caps = (dbmsByType[s.dbType] && dbmsByType[s.dbType].supportsFeatures) || {};
        const supportsCancel = !!caps.cancelQuery;
        const supportsVerify = !!caps.verifySession;
        nodeContent = (
            <div className={`${styles.nodeContent} ${styles.sessionNode}`}>
                <span className={`${styles.sessionStatusDot} ${dotClass}`} />
                <span className={styles.sessionLabel} title={labelText}>{labelText}</span>
                {sid != null && <span className={styles.sessionSid}>sid {sid}</span>}
                {isRunning && s.currentQuery && (
                    <Elapsed startedAt={s.currentQuery.startedAt} className={styles.sessionElapsed} />
                )}
                {isCancelling && <span className={styles.sessionCancelling}>cancelling…</span>}
                <span className={styles.connActions}>
                    {supportsVerify && (
                        <span className={styles.iconTipWrap} title={t('common:exp.verifyState')}>
                            <FiHelpCircle
                                className={styles.connActionIcon}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    if (tree.props.onVerifySession) tree.props.onVerifySession(s, rect);
                                }}
                            />
                        </span>
                    )}
                    {supportsCancel && (
                        <span className={styles.iconTipWrap} title={isRunning ? t('common:exp.cancelSql') : t('common:exp.notRunning')}>
                            <FiSquare
                                className={isRunning ? styles.connActionIconDanger : styles.connActionIconDisabled}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (isRunning && tree.props.onCancelSession) tree.props.onCancelSession(s);
                                }}
                            />
                        </span>
                    )}
                    <span className={styles.iconTipWrap} title={t('common:exp.endSession')}>
                        <FiX
                            className={styles.connActionIconDanger}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (tree.props.onCloseSession) tree.props.onCloseSession(s);
                            }}
                        />
                    </span>
                </span>
            </div>
        );
    } else if (node.data.type === 'sessions-folder') {
        // children 은 실제 세션만 포함 (add-session 노드 제거됨, + 아이콘으로 대체).
        const sessionCount = Array.isArray(node.data.children) ? node.data.children.length : 0;
        const onAddClick = (e) => {
            e.stopPropagation(); // node toggle 방지 — + 만 동작
            if (tree.props.onCreateSession && node.data.connectionId) {
                tree.props.onCreateSession(node.data.connectionId);
                // Sessions 폴더가 닫혀있으면 자동으로 펼쳐서 새로 추가된 세션이 즉시 보이게 함.
                if (!node.isOpen) {
                    try { node.open(); } catch (_) { /* react-arborist 버전 차이 방어 */ }
                }
            }
        };
        nodeContent = (
            <div className={styles.nodeContent}>
                <span className={styles.nodeName}>Sessions</span>
                <span className={styles.sessionsFolderCount}>({sessionCount})</span>
                <span className={styles.iconTipWrap} title={t('common:exp.addSession')}>
                    <FiPlus
                        className={styles.sessionsFolderAddIcon}
                        onClick={onAddClick}
                        style={{ marginLeft: 6, color: '#3b82f6', cursor: 'pointer' }}
                    />
                </span>
            </div>
        );
    } else if (node.data.type === 'schemas-folder') {
        nodeContent = (
            <div className={styles.nodeContent}>
                <span className={styles.nodeName}>Schemas</span>
                {node.data.isLoadingSchemas && <span className={styles.loadingText}> (Loading...)</span>}
                {node.data.schemasError && <span className={styles.errorText}> (Error)</span>}
            </div>
        );
    } else if (node.data.type === 'connection') {
        const isConnected = node.data.isConnected !== false;
        const isBuiltin = node.data.builtin === true;
        // schemasError 가 있으면 사실상 healthy 가 아님 — 사용자에게 재연결 버튼 노출.
        const showAsHealthy = isConnected && !node.data.schemasError;
        // 빌트인 연결: 새로고침 · 편집(이름·설명·읽기 전용만 — 2026-10-28). 해제·삭제는 의미 없음.
        // 일반 연결: 상태에 따라 4종 모두.
        const connActions = isBuiltin ? (
            <span className={styles.connActions}>
                <span className={styles.iconTipWrap} title={t('common:exp.refreshSchema')}>
                    <FiRefreshCw
                        className={styles.connActionIcon}
                        onClick={(e) => { e.stopPropagation(); if (tree.props.onRefreshSchemas) tree.props.onRefreshSchemas(node.data); }}
                    />
                </span>
                <span className={styles.iconTipWrap} title={t('common:exp.editConnection')}>
                    <FiEdit2
                        className={styles.connActionIcon}
                        onClick={(e) => { e.stopPropagation(); if (tree.props.onEditConnection) tree.props.onEditConnection(node.data); }}
                    />
                </span>
            </span>
        ) : (
            <span className={styles.connActions}>
                {!showAsHealthy && (
                    <span className={styles.iconTipWrap} title={node.data.schemasError ? t('common:exp.reconnect') : t('common:exp.connect')}>
                        <FiLink
                            className={styles.connActionIconSuccess}
                            onClick={(e) => { e.stopPropagation(); if (tree.props.onReconnect) tree.props.onReconnect(node.data); }}
                        />
                    </span>
                )}
                {showAsHealthy && (
                    <span className={styles.iconTipWrap} title={t('common:exp.refreshSchema')}>
                        <FiRefreshCw
                            className={styles.connActionIcon}
                            onClick={(e) => { e.stopPropagation(); if (tree.props.onRefreshSchemas) tree.props.onRefreshSchemas(node.data); }}
                        />
                    </span>
                )}
                {showAsHealthy && (
                    <span className={styles.iconTipWrap} title={t('common:exp.disconnect')}>
                        <FiXCircle
                            className={styles.connActionIcon}
                            onClick={(e) => { e.stopPropagation(); if (tree.props.onDisconnect) tree.props.onDisconnect(node.data); }}
                        />
                    </span>
                )}
                <span className={styles.iconTipWrap} title={t('common:exp.editConnection')}>
                    <FiEdit2
                        className={styles.connActionIcon}
                        onClick={(e) => { e.stopPropagation(); if (tree.props.onEditConnection) tree.props.onEditConnection(node.data); }}
                    />
                </span>
                <span className={styles.iconTipWrap} title={t('common:exp.removeFromList')}>
                    <FiTrash2
                        className={styles.connActionIconDanger}
                        onClick={(e) => { e.stopPropagation(); if (tree.props.onRemoveConnection) tree.props.onRemoveConnection(node.data); }}
                    />
                </span>
            </span>
        );

        if (node.data.isLoadingSchemas) {
            nodeContent = (
                <div className={styles.nodeContent}>
                    <span className={styles.nodeName}>{node.data.name}</span>
                    <span className={styles.loadingText}> (Loading...)</span>
                    {connActions}
                </div>
            );
        } else if (node.data.schemasError) {
            nodeContent = (
                <div className={styles.nodeContent}>
                    <span className={styles.nodeName}>{node.data.name}</span>
                    <span className={styles.errorText}> (Error)</span>
                    {connActions}
                </div>
            );
        } else {
            nodeContent = (
                <div className={styles.nodeContent}>
                    <span className={styles.nodeName} style={!isConnected ? { opacity: 0.5, fontStyle: 'italic' } : {}}>{node.data.name}</span>
                    {!isConnected && <span className={styles.disconnectedBadge}>{t('common:exp.disconnected')}</span>}
                    {/* **읽기 전용이면 눌러 보기 전에 알아야 한다.** 서버가 이 사람에게 적용될
                        값을 보내 준다(공용 연결은 관리자가 아니면 읽기 전용) — 저장된 설정이
                        아니라 실제로 열릴 모습이다. */}
                    {node.data.readOnly && (
                        <span
                            className={styles.readOnlyBadge}
                            title={node.data.readOnlyReason === 'roAccount'
                                ? t('common:exp.readOnlyAccountTip', { user: node.data.effectiveUser || '' })
                                : t(node.data.readOnlyReason === 'notAdmin'
                                    ? 'common:exp.readOnlyNotAdminTip' : 'common:exp.readOnlyTip')}
                        >
                            {t('common:exp.readOnly')}
                        </span>
                    )}
                    {connActions}
                </div>
            );
        }
    }

    // react-arborist가 클릭 이벤트를 내부적으로 처리하여 onActivate를 호출해줌.
    // 하지만 명시적으로 onClick 핸들러를 추가해서 디버깅해보자
    return (
        <div 
            style={style} 
            ref={dragHandle} 
            className={`${styles.node} ${node.state.isSelected ? styles.selected : ''}`}
            onClick={handleClick} // 디버깅을 위해 다시 활성화
        >
            <Icon />
            {nodeContent}
        </div>
    );
}

Node.propTypes = {
    node: PropTypes.object.isRequired,
    style: PropTypes.object,
    dragHandle: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]),
    tree: PropTypes.object.isRequired,
};

const SchemaExplorer = ({
    onNodeActivate, onERDClick, initialData,
    onOpenConnectionModal, onEditConnection, onDisconnect, onReconnect, onRemoveConnection, onRefreshSchemas,
    // 연결 › DB 연결 관리 즐겨찾기 — 끄면 머리말의 「연결」 버튼만 숨긴다(빈 화면 안내 버튼은 그대로)
    showConnectButton = true,
    // Phase 2: session 관련 핸들러
    onCreateSession, onCancelSession, onCloseSession,
    // verify (V$SESSION 조회)
    onVerifySession,
    // Phase A: DBMS manifest 인덱스 (dbType → manifest). 세션 노드 capability 분기에 사용.
    dbmsByType,
    // 탐색기 접기(2026-10-10) — 주면 머리줄 끝에 「접기」 버튼. 접힌 모양(아이콘 띠)은 App 의 ExplorerRail
    onCollapse,
}) => {
    const { t } = useTranslation();
    const [treeHeight, setTreeHeight] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const containerRef = useRef(null);

    useLayoutEffect(() => {
        // console.log('[SchemaExplorer] useLayoutEffect: Calculating height. Current containerRef:', containerRef.current);
        if (containerRef.current) {
            // console.log('[SchemaExplorer] Container clientHeight:', containerRef.current.clientHeight);
            setTreeHeight(containerRef.current.clientHeight);
        }

        const resizeObserver = new ResizeObserver(entries => {
            for (let entry of entries) {
                if (entry.target === containerRef.current) {
                    // console.log('[SchemaExplorer] ResizeObserver: New clientHeight:', entry.contentRect.height);
                    setTreeHeight(entry.contentRect.height);
                }
            }
        });

        if (containerRef.current) {
            resizeObserver.observe(containerRef.current);
        }

        return () => {
            if (containerRef.current) {
                // eslint-disable-next-line react-hooks/exhaustive-deps
                resizeObserver.unobserve(containerRef.current); 
            }
            resizeObserver.disconnect();
        };
    }, [initialData]); // initialData 변경 시 재계산 (레이아웃 변경 가능성)

    // console.log('[SchemaExplorer] Rendering with treeHeight:', treeHeight);

    return (
        <div ref={containerRef} className={styles.schemaExplorerContainer}>
            <div className={styles.explorerToolbar}>
                <span className={styles.explorerTitle}>{t('common:exp.title')}</span>
                {showConnectButton && (
                    <button
                        className={styles.explorerToolbarBtn}
                        onClick={onOpenConnectionModal}
                        title={t('common:exp.manageConnections')}
                    >
                        <FaDatabase style={{ marginRight: 3 }} /> {t('common:exp.connect')}
                    </button>
                )}
                {onCollapse && (
                    <button
                        type="button"
                        className={styles.explorerCollapseBtn}
                        onClick={onCollapse}
                        title={t('common:exp.collapse')}
                        aria-label={t('common:exp.collapse')}
                    >
                        <FiChevronsLeft size={13} />
                    </button>
                )}
            </div>
            {/* 트리 노드 이름 필터 — 입력하면 react-arborist 가 매칭되는 노드 + 조상만 표시 */}
            <div className={styles.explorerSearchBar}>
                <FiSearch size={11} className={styles.explorerSearchIcon} />
                <input
                    type="search"
                    className={styles.explorerSearchInput}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder={t('common:exp.searchPlaceholder')}
                    title={t('common:exp.searchHint')}
                />
                {searchTerm && (
                    <button
                        type="button"
                        className={styles.explorerSearchClear}
                        onClick={() => setSearchTerm('')}
                        title={t('common:exp.clearSearch')}
                    >
                        <FiX size={11} />
                    </button>
                )}
            </div>
            {/* 연결이 하나도 없을 때 — 빈 트리만 보여주면 로딩 실패인지 미설정인지 알 수 없다.
                (예전에는 'Oracle SQL 실습 DB' 더미가 이 자리를 채웠지만, 백엔드에 없는 id 라
                 자동 스키마 조회가 실패해 (Error) 로 표시되는 부작용이 있어 제거했다.) */}
            {(!initialData || initialData.length === 0) && (
                <div className={styles.explorerEmptyState}>
                    <p>{t('common:exp.noConnections')}</p>
                    {/* 즐겨찾기와 무관하게 늘 보인다 — 연결이 하나도 없을 때의 유일한 입구다 */}
                    {onOpenConnectionModal && (
                        <button type="button" onClick={onOpenConnectionModal}>
                            {t('common:exp.addConnection')}
                        </button>
                    )}
                    <span>{t('common:exp.checkBackend')}</span>
                </div>
            )}
            {/* Tree 컴포넌트가 0보다 큰 높이를 가질 때만 렌더링 (선택적 최적화) */}
            {treeHeight > 0 && (
                <Tree
                    data={initialData}
                    // react-arborist 는 이 className 을 react-window 의 FixedSizeList 스크롤
                    // 컨테이너(실제 overflow:auto 걸린 엘리먼트) 에 그대로 넘겨준다 — 여기 말고는
                    // 그 엘리먼트를 CSS 로 건드릴 방법이 없다(react-window 가 내부적으로 렌더링).
                    // scrollbar-gutter: stable 로 스크롤바 유무와 무관하게 항상 그 공간을 확보해,
                    // 하위 스키마가 펼쳐져 스크롤이 생겨도 오른쪽 아이콘 열이 밀리지 않게 한다.
                    className={styles.treeScroll}
                    openByDefault={false}
                    /* 2자 미만이면 빈 문자열로 → 필터링 안 함. 1자 입력 시 거의 모든 노드 매칭으로 의미 없는 부하 방지. */
                    searchTerm={searchTerm.trim().length >= 2 ? searchTerm.trim() : ''}
                    searchMatch={(node, term) => {
                        const t = String(term || '').toLowerCase();
                        if (t.length < 2) return true;
                        const name = String(node.data?.name || '').toLowerCase();
                        return name.includes(t);
                    }}
                    width={"100%"}
                    height={treeHeight} // 측정된 숫자 높이 사용
                    indent={24}
                    rowHeight={22}
                    onERDClick={onERDClick}
                    onEditConnection={onEditConnection}
                    onDisconnect={onDisconnect}
                    onReconnect={onReconnect}
                    onRemoveConnection={onRemoveConnection}
                    onRefreshSchemas={onRefreshSchemas}
                    // Phase 2: 세션 관련 핸들러 — Node 컴포넌트가 tree.props 로 접근
                    onCreateSession={onCreateSession}
                    onCancelSession={onCancelSession}
                    onCloseSession={onCloseSession}
                    onVerifySession={onVerifySession}
                    dbmsByType={dbmsByType}
                    onActivate={(node) => {
                        const t = node.data?.type;
                        // connection / schema / folder / sessions-folder / schemas-folder 토글
                        if (
                            t === 'connection' || t === 'schema' || t === 'folder' ||
                            t === 'sessions-folder' || t === 'schemas-folder'
                        ) {
                            const wasOpen = node.isOpen;
                            node.toggle();
                            // connection 을 새로 펼치면 그 아래 Sessions 폴더도 자동 펼침 —
                            // 사용자가 세션 목록을 한눈에 확인 가능. 다음 tick 에 호출 (toggle 반영 후).
                            if (t === 'connection' && !wasOpen) {
                                const sessionsFolderId = `sessions-folder:${node.data.id}`;
                                setTimeout(() => {
                                    try { node.tree?.open?.(sessionsFolderId); } catch (_) { /* ignore */ }
                                }, 0);
                            }
                        }
                        if (onNodeActivate && node.data) {
                            onNodeActivate(node.data); // App.js의 handleSchemaNodeActivate 호출
                        }
                    }}
                >
                    {Node}
                </Tree>
            )}
        </div>
    );
};

SchemaExplorer.propTypes = {
    onNodeActivate: PropTypes.func.isRequired,
    onERDClick: PropTypes.func,
    initialData: PropTypes.array.isRequired,
    onOpenConnectionModal: PropTypes.func,
    onEditConnection: PropTypes.func,
    onDisconnect: PropTypes.func,
    onReconnect: PropTypes.func,
    onRemoveConnection: PropTypes.func,
    // Phase 2
    onCreateSession: PropTypes.func,
    onCancelSession: PropTypes.func,
    onCloseSession: PropTypes.func,
    // verify (V$SESSION 조회)
    onVerifySession: PropTypes.func,
    // Phase A: DBMS manifest 인덱스 (capability flag 분기용)
    dbmsByType: PropTypes.object,
    onCollapse: PropTypes.func,
};

export default SchemaExplorer; 